import React, { useEffect, useRef, useState } from 'react'
import {
  Typography,
  IconButton,
  Box,
  useMediaQuery,
} from '@material-ui/core'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import {
  Timeslot as BackendTimeslot,
  ScheduleType,
} from '../../../api/BookingCalendar'
import { Day } from '../Calendar'
import { getDaysForRange, CalendarContextType } from './changeDaysOfMonth'
import useMonthWalker from '../../../hooks/useMonthWalker'
import { MonthlyMatrix } from './MonthlyMatrix'
import MonthlyCalendarDay from './MonthlyCalendarDay'
import {
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  getDay,
  format,
} from 'date-fns'
import { useOrderContext } from '../../NewOrderFlow/OrderContext'
import WarningMessage, { WarningMessageColor } from '../../WarningMessage/WarningMessage'
import LoadingSpinner from '../../SearchBar/LoadingSpinner'
import theme, { COLORS } from '../../../styles/theme'
import classes from './MonthlyCalendar.styles'
export type Meridiem = 'am' | 'pm'

type CalendarProps = {
  fullPremise: any
  onTimeslotChange?: (timeslot: BackendTimeslot | null) => any
  broadbandProduct?: string
  scheduleType?: ScheduleType
}

export default function MonthlyCalendar({
  onTimeslotChange,
  fullPremise,
  broadbandProduct,
  scheduleType,
}: CalendarProps) {
  const { offset, month, year, nextMonth, prevMonth } = useMonthWalker()
  const { options } = useOrderContext()
  const { wier } = options
  const serviceType = wier?.productGroup ? 'Premium' : 'Standard'

  const [days, setDays] = useState<Day[]>([])
  const [days1, setDays1] = useState<Day[]>([])
  const [selectedDay, setSelectedDay] = useState<Day | null>(null)
  const [selectedTimeIndex, setSelectedTimeIndex] = useState<number>()
  const [selectedMeridiem, setSelectedMeridiem] = useState<Meridiem>()
  const [selectedTimeslot, setSelectedTimeslot] = useState<BackendTimeslot | null>()
  const [showTimeSlots, setShowTimeSlots] = useState(false)
  const [activeDayIndex, setActiveDayIndex] = useState<number | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [loadingTimeslots, setLoadingTimeslots] = useState(false)
  const [errorMessage, setErrorMessage] = useState(false)
  const cachedDays = useRef<Record<number, Day[]>>({})


  useEffect(() => {
    cachedDays.current[offset] = days
  }, [offset, days])

  const normalizeDays = (inputDays: Day[]): Day[] => {
    return inputDays.map((day) => {
      const amSlots = day.timeslots?.am || []
      const pmSlots = day.timeslots?.pm || []
      const hasAvailableSlot = [...amSlots, ...pmSlots].some(
        (s) => s.timeslot?.available
      )
      return {
        ...day,
        available: hasAvailableSlot,
      }
    })
  }
  useEffect(() => {
    const allWeekTimeslots: boolean[] = []

    if (days.length === 0) {
      setLoadingTimeslots(true)
      return
    }

    const hasApiError = days.some(day =>
      Object.values(day.timeslots).some(slots =>
        slots.some(slot =>
          slot.timeslot?.appointmentWindowId?.length === 0 ||
          (slot.timeslot?.data && slot.timeslot.data.some(d => d._status === true)),
        ),
      ),
    )

    if (hasApiError) {
      setLoadingTimeslots(false)
      setErrorMessage(true)
      return
    }

    days.forEach((day) => {
      const meridies = Object.keys(day.timeslots)
      meridies.forEach((meridiem: Meridiem) => {
        day.timeslots[meridiem].forEach((timeslot) => {
          if (timeslot.timeslot?.available !== undefined) {
            allWeekTimeslots.push(timeslot.timeslot.available)
          }
        })
      })
    })

    if (allWeekTimeslots.length > 0) {
      setLoadingTimeslots(false)
      setErrorMessage(false)
    }

    const errorTimeout = setTimeout(() => {
      setErrorMessage(days.length === 0)
    }, 30000)

    return () => clearTimeout(errorTimeout)
  }, [days])

  const currentBaseDate = addMonths(new Date(), offset)
  const currentBaseMonth = format(currentBaseDate, 'MMM')
  const nextDate = addMonths(currentBaseDate, 1)
  const monthCache = useRef(new Map<string, Day[]>())
  const getCacheKey = (y: number, m: number) => `${y}-${String(m + 1).padStart(2, '0')}`

  const getDaysWithCache = async (
    args: Omit<CalendarContextType, 'setDays'>,
    y: number,
    m: number
  ): Promise<Day[]> => {
    const key = getCacheKey(y, m)
    if (monthCache.current.has(key)) {
      return monthCache.current.get(key)!
    } else {
      setLoadingTimeslots(true)
      const result = await getDaysForRange(args)
      monthCache.current.set(key, result)
      return result
    }
  }


  useEffect(() => {
    const load = async () => {
      setLoadingTimeslots(true)

      const [res0, res1] = await Promise.all([
        getDaysWithCache({
          fullPremise,
          startDate: startOfMonth(currentBaseDate),
          endDate: endOfMonth(currentBaseDate),
          selectedTimeslot: selectedTimeslot ?? undefined,
          serviceType,
          broadbandProduct,
          scheduleType,
        }, currentBaseDate.getFullYear(), currentBaseDate.getMonth()),

        getDaysWithCache({
          fullPremise,
          startDate: startOfMonth(nextDate),
          endDate: endOfMonth(nextDate),
          selectedTimeslot: selectedTimeslot ?? undefined,
          serviceType,
          broadbandProduct,
          scheduleType,
        }, nextDate.getFullYear(), nextDate.getMonth()),
      ])

      const normalized = normalizeDays(res0)
      const normalized1 = normalizeDays(res1)
      setDays(normalized)
      setDays1(normalized1)
      setLoadingTimeslots(false)
    }

    load()
  }, [offset, fullPremise, selectedTimeslot, broadbandProduct, serviceType, scheduleType, year, month])

  const generatePrevTail = (
    offset: number,
    currentBaseDate: Date,
    prevMonthDays: Day[]
  ): Day[] => {
    const weekdayIndex = getDay(startOfMonth(currentBaseDate))
    const monIndex = (weekdayIndex + 6) % 7
    if (monIndex === 0) return []

    if (offset === 1 && prevMonthDays.length > 0) {
      return prevMonthDays.slice(-monIndex).map(d => ({
        ...d,
        isPlaceholder: true,
      }))
    }


    const prevMonthDate = subMonths(currentBaseDate, 1)
    const prevMonthName = format(prevMonthDate, 'LLL')
    const lastDay = endOfMonth(prevMonthDate).getDate()

    return Array.from({ length: monIndex }, (_, i) => ({
      date: (lastDay - monIndex + 1 + i).toString(),
      month: prevMonthName,
      available: false,
      isPlaceholder: true,
      timeslots: { am: [], pm: [] },
      dayOfWeek: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
      hasSelectedTimeslot: false,
    }))
  }

  const getNextTail = (
    mainDays: Day[],
    nextMonthDays: Day[],
    prevTail: Day[],
    currentBaseDate: Date
  ): Day[] => {
    const full = [...prevTail, ...mainDays]
    const remainder = full.length % 7
    if (remainder === 0) return []

    const tailLength = 7 - remainder
    const dayOfWeekIndexStart = (remainder) % 7

    return Array.from({ length: tailLength }, (_, i) => {
      const date = (i + 1).toString()

      const realDay = nextMonthDays.find(d =>
        d.date === date
      )

      if (realDay) {
        return {
          ...realDay,
        }
      }

      return {
        date,
        month: format(addMonths(currentBaseDate, 1), 'LLL'),
        available: false,
        isPlaceholder: true,
        timeslots: { am: [], pm: [] },
        dayOfWeek: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][(dayOfWeekIndexStart + i) % 7],
        hasSelectedTimeslot: false,
      }
    })
  }

  const prevMonthDays: Day[] = cachedDays.current[offset - 1] ?? []
  const nextMonthDays: Day[] = offset === 0 ? days1 : (cachedDays.current[offset + 1] ?? [])
  const prevTail: Day[] = generatePrevTail(offset, currentBaseDate, prevMonthDays)
  const nextTail: Day[] = getNextTail(days, nextMonthDays, prevTail, currentBaseDate)
  const fullDays: Day[] = [...prevTail, ...days, ...nextTail]

  useEffect(() => {
    if (!selectedDay) {
      setActiveDayIndex(null)
      setShowTimeSlots(false)
      return
    }

    const idx = fullDays.findIndex(d =>
      d.date === selectedDay.date && d.month === selectedDay.month
    )

    if (idx !== -1) {
      setActiveDayIndex(idx)
      setShowTimeSlots(true)
    } else {
      setActiveDayIndex(null)
      setShowTimeSlots(false)
    }
  }, [fullDays, selectedDay])


  useEffect(() => {
    if (!selectedDay) {
      setShowTimeSlots(false)
      return
    }

    const isDayInCurrentCalendar = fullDays.some(
      (d) => d.date === selectedDay.date && d.month === selectedDay.month
    )

    setShowTimeSlots(isDayInCurrentCalendar)
  }, [offset, selectedDay, currentBaseDate, fullDays])

  useEffect(() => {
    if (isInitialized || days.length === 0) return

    const today = new Date()
    const todayDate = today.getDate().toString()
    const todayMonth = format(today, 'LLL')
    const todayYear = today.getFullYear()

    const todayIndexInDays = days.findIndex(
      (day) =>
        day.date === todayDate &&
        day.month === todayMonth &&
        day.available &&
        todayYear === year
    )

    if (todayIndexInDays !== -1) {
      const todayIndexInFullDays = prevTail.length + todayIndexInDays
      handleDayClick(fullDays[todayIndexInFullDays], todayIndexInFullDays)
    } else {
      const firstAvailableDayIndex = days.findIndex((day) => day.available)
      if (firstAvailableDayIndex !== -1) {
        const firstAvailableDayInFullDays = prevTail.length + firstAvailableDayIndex
        handleDayClick(fullDays[firstAvailableDayInFullDays], firstAvailableDayInFullDays)
      }
    }

    setIsInitialized(true)
  }, [days, year, prevTail, fullDays])
  const handleDayClick = (day: Day, index: number) => {
    setSelectedDay(day)
    setActiveDayIndex(index)
    setSelectedMeridiem(undefined)
    setSelectedTimeIndex(undefined)
    setSelectedTimeslot(null)
    onTimeslotChange?.(null)
    const isDayInCurrentCalendar = fullDays.some(
      (d) => d.date === day.date && d.month === day.month
    )
    setShowTimeSlots(isDayInCurrentCalendar)
  }


  const changeTimeslot = async ({
    day,
    meridiem,
    timeIndex,
  }: {
    day: Day
    meridiem?: Meridiem
    timeIndex?: number
  }) => {
    if (meridiem && timeIndex !== undefined) {
      const slot = day.timeslots?.[meridiem]?.[timeIndex]
      if (slot && slot.timeslot?.available) {
        setSelectedDay(day)
        setSelectedMeridiem(meridiem)
        setSelectedTimeIndex(timeIndex)
        setSelectedTimeslot(slot.timeslot)
        onTimeslotChange?.(slot.timeslot)
      }
    }
  }

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const monthLabels: { [key: string]: string } = {
    JAN: 'January',
    FEB: 'February',
    MAR: 'March',
    APR: 'April',
    MAY: 'May',
    JUN: 'June',
    JUL: 'July',
    AUG: 'August',
    SEP: 'September',
    OCT: 'October',
    NOV: 'November',
    DEC: 'December',
  }
  const apiMonthCode = format(currentBaseDate, 'LLL').toUpperCase()
  const monthLabel = monthLabels[apiMonthCode]
  const shouldShowNextArrow = (currentBaseDate: Date): boolean => {
    const today = new Date()
    const isSameMonth = today.getMonth() === currentBaseDate.getMonth() && today.getFullYear() === currentBaseDate.getFullYear()
    return isSameMonth && today.getDate() > 7
  }

  return (
    <Box className={classes.calendarWrapper} id='monthly-calendar'>
      <Box className={classes.monthToggleWrapper}>
        <IconButton
          onClick={prevMonth}
          style={{ visibility: offset === 1 ? 'visible' : 'hidden' }}
        >
          <ArrowForwardIosIcon style={{ color: COLORS.primary, transform: 'rotate(180deg)' }} fontSize='small' />
        </IconButton>

        <Typography variant='h4' style={{ fontSize: 20 }} color="primary">
          {monthLabel}
        </Typography>

        <IconButton
          onClick={nextMonth}
          style={{ visibility: offset === 0 ? (shouldShowNextArrow(currentBaseDate) ? 'visible' : 'hidden') : 'hidden' }}
        >
          <ArrowForwardIosIcon style={{ color: COLORS.primary }} fontSize='small' />
        </IconButton>
      </Box>

      {days.length > 0 && !loadingTimeslots ? (
        <>
          <MonthlyMatrix
            fullDays={fullDays}
            currentDay={new Date().getDate().toString()}
            offset={offset}
            currentMonth={currentBaseMonth}
            year={year}
            selectedDayIndex={activeDayIndex ?? 0}
            selectedTimeIndex={selectedTimeIndex}
            selectedMeridiem={selectedMeridiem}
            selectedDay={selectedDay ?? undefined}
            changeTimeslot={changeTimeslot}
            onDayClick={handleDayClick}
          />
          {showTimeSlots && activeDayIndex !== null && fullDays[activeDayIndex] && (
            <Box display="flex" alignItems={isMobile ? 'flex-start' : 'center'} flexDirection="column" mt={3}>
              <Typography variant="h4" color='primary'>Select time</Typography>
              <MonthlyCalendarDay
                day={fullDays[activeDayIndex]}
                currentDayIndex={activeDayIndex}
                changeTimeslot={changeTimeslot}
                selectedDayIndex={activeDayIndex}
                selectedTimeIndex={selectedTimeIndex}
                selectedMeridiem={selectedMeridiem}
                selectedDay={selectedDay ?? undefined}
                selectedTimeSlot={selectedTimeslot ?? undefined}
              />
            </Box>
          )}
        </>
      ) : (
        <Box>
          {errorMessage ? (
            <WarningMessage
              color={WarningMessageColor.yellow}
              text="<strong>Sorry, we are having some trouble loading the calendar.</strong><br>Please try again later."
            />
          ) : (
            <LoadingSpinner />
          )}
        </Box>
      )}
    </Box>
  )
}
